'use client';
import { useState, useEffect, useRef } from "react";
import { getYmmFromStorage } from "../../utils/ymm-storage";
import { ymmApiClient } from "../../api/client";
import { parseFitmentLabelHtml } from "../../api/parser";
import { ParsedFitmentLabelResponse, YmmFitmentLabelApiResponse, YmmSearchParams } from "../../api/types";
import { Button } from "@/vibes/soul/primitives/button";
import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { AlertTriangleIcon, CheckIcon } from "lucide-react";
import * as Skeleton from '@/vibes/soul/primitives/skeleton';

export function FitmentBanner({ productId }: { productId: string }) {
  const userSelection = getYmmFromStorage();

  if (!userSelection) {
    return;
  };

  const [fitmentData, setFitmentData] = useState<ParsedFitmentLabelResponse | null>(null);
  const client = useRef(ymmApiClient());
  const router = useRouter();
  const locale = useLocale();
  const base = locale ? `/${locale}/fitments` : '/fitments';
  const [url, setUrl] = useState<string>('#');

  useEffect(() => {
    const ymmFromStorage = storedToSearchParams(userSelection);
    setUrl(`${base}?yr=${encodeURIComponent(ymmFromStorage.yr ?? '')}&mk=${encodeURIComponent(ymmFromStorage.mk ?? '')}&md=${encodeURIComponent(ymmFromStorage.md ?? '')}`);

    client.current.getFitmentLabelData(productId, Object.values(userSelection).join('~')).then((data: YmmFitmentLabelApiResponse) => {
      setFitmentData(parseFitmentLabelHtml(data));
    });

  }, [client, productId]);

  const fitmentBannerSkeleton = (
    <div className="flex items-center justify-between bg-[hsl(var(--contrast-700)/0.2)] p-3 @3xl:p-2 @5xl:p-3 mb-3">
      <div className="flex flex-col @3xl:flex-row items-center gap-3">
        <div className="flex items-center">
          <Skeleton.Box className="w-6 h-6" />
          <Skeleton.Text characterCount={11} className="text-2xl ml-3 @5xl:mr-5" />
        </div>
        <Skeleton.Text characterCount={20} className="text-base text-center @3xl:text-left mb-4 @3xl:mb-0" />
      </div>
      <Skeleton.Box className="w-44 h-12" />
    </div>
  );
  const viewItemsThatFitButton = (url: string) => {
    return (
      <Button
        variant="primary"
        size="small"
        shape="square"
        className="!px-3"
        onClick={() => {
          router.push(url);
        }}
      >
        View Items That Fit
      </Button>
    );
  };

  return (
    <>
      {!fitmentData && fitmentBannerSkeleton}
      {!fitmentData?.appIsDisabled && fitmentData?.productFit === 'confirm-fit' && !fitmentData.appIsDisabled && (
        <div className="flex flex-col @3xl:flex-row items-center justify-between bg-[hsl(var(--contrast-700)/0.2)] p-3 @3xl:p-2 @5xl:p-3 mb-3">
          <div className="flex flex-col @3xl:flex-row items-center gap-3">
            <div className="flex items-center">
              <CheckIcon size={24} />
              <h2 className="font-[family-name:var(--font-family-heading)] text-2xl ml-3 @5xl:mr-5">{fitmentData.labelFit}</h2>
            </div>
            <p className="text-center @3xl:text-left mb-4 @3xl:mb-0">{fitmentData.descriptionFit}</p>
          </div>
          {viewItemsThatFitButton(url)}
        </div>
      )}

      {!fitmentData?.appIsDisabled && fitmentData?.productFit === 'not-fit' && (
        <div className="flex flex-col @3xl:flex-row items-center justify-between bg-[hsl(var(--warning)/0.2)] p-3 @3xl:p-2 @5xl:p-3 mb-3">
          <div className="flex flex-col @3xl:flex-row items-center gap-3">
            <div className="flex items-center">
              <AlertTriangleIcon size={24} />
              <h2 className="font-[family-name:var(--font-family-heading)] text-2xl ml-3 @5xl:mr-5">{fitmentData.labelDontFit}</h2>
            </div>
            <p className="text-center @3xl:text-left mb-4 @3xl:mb-0">{fitmentData.descriptionDontFit}</p>
          </div>
          {viewItemsThatFitButton(url)}
        </div>
      )}

      {!fitmentData?.appIsDisabled && fitmentData?.productFit === 'universal-fit' && (
        <div className="flex flex-col @3xl:flex-row items-center justify-between bg-[hsl(var(--info)/0.2)] p-3 @3xl:p-2 @5xl:p-3 mb-3">
          <div className="flex flex-col @3xl:flex-row items-center gap-3">
            <div className="flex items-center">
              <CheckIcon size={24} />
              <h2 className="font-[family-name:var(--font-family-heading)] text-2xl ml-3 @5xl:mr-5">{fitmentData.labelFit}</h2>
            </div>
            <p className="text-center @3xl:text-left mb-4 @3xl:mb-0">{fitmentData.descriptionFit}</p>
          </div>
          {viewItemsThatFitButton(url)}
        </div>
      )}
    </>
  );
};

function stripTagPrefix(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

function storedToSearchParams(stored: { year: string; make: string; model: string }): YmmSearchParams {
  return {
    yr: stripTagPrefix(stored.year, 'yr_'),
    mk: stripTagPrefix(stored.make, 'mk_'),
    md: stripTagPrefix(stored.model, 'md_'),
  };
}